/**
 * PositionDetailModal — composes EntityDetailModal for a single posting.
 *
 * Replaces the old "click row → navigate to /applications/preview" hop.
 * Modal lets the user save / mark-considering / disqualify a posting and
 * either jump to the source posting or open the full preview page.
 *
 * Usage:
 *   <PositionDetailModal
 *     open={!!activePosition}
 *     onClose={() => setActive(null)}
 *     position={activePosition}
 *   />
 *
 * Selection state is read from the SelectionsProvider that wraps
 * PositionsTab; no prop drilling required.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LaunchIcon from '@mui/icons-material/Launch';
import { SAGE, CREAM, WARM } from '../../theme';
import { useSelections } from '../../hooks/useSelections';
import EntityDetailModal from '../entity/EntityDetailModal';
import SelectionToggle from '../entity/SelectionToggle';

const F = "'Outfit', sans-serif";
const SERIF = "'Lora', Georgia, serif";

const formatPostedDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days < 1) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return d.toLocaleDateString();
};

const formatSalary = (min, max) => {
  if (!min && !max) return null;
  const toK = (n) => `$${Math.round(n / 1000)}k`;
  if (min && max) return `${toK(min)} – ${toK(max)}`;
  return toK(min || max);
};

const PositionDetailModal = ({ open, onClose, position }) => {
  const navigate = useNavigate();
  const { getState, cycle } = useSelections('positions');

  if (!position) return null;
  const id = position.job_id;
  const state = getState(id);
  const salary = formatSalary(position.salary_min, position.salary_max);

  const header = (
    <Stack spacing={1}>
      <Typography
        sx={{
          fontFamily: F, fontSize: '0.65rem', fontWeight: 700,
          letterSpacing: 1.2, textTransform: 'uppercase', color: SAGE[500],
        }}
      >
        Open position
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, flexWrap: 'wrap' }}>
        <Typography sx={{ fontFamily: SERIF, fontSize: '1.4rem', fontWeight: 600, color: SAGE[800], lineHeight: 1.25 }}>
          {position.title}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontFamily: F, fontSize: '0.85rem', fontWeight: 600, color: SAGE[700] }}>
          {position.company_name}
        </Typography>
        <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: SAGE[300] }} />
        <Typography sx={{ fontFamily: F, fontSize: '0.78rem', color: SAGE[600] }}>
          {position.location || (position.markets || []).join(', ')}
        </Typography>
        {position.level && (
          <>
            <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: SAGE[300] }} />
            <Chip
              label={position.level}
              size="small"
              sx={{ bgcolor: SAGE[100], color: SAGE[700], fontFamily: F, fontSize: '0.7rem', fontWeight: 600, height: 22 }}
            />
          </>
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.5 }}>
        {salary && (
          <Typography sx={{ fontFamily: F, fontSize: '0.78rem', color: SAGE[600] }}>
            <Box component="span" sx={{ fontWeight: 700 }}>{salary}</Box>
            <Box component="span" sx={{ color: SAGE[400], ml: 0.5 }}>· base</Box>
          </Typography>
        )}
        <Typography sx={{ fontFamily: F, fontSize: '0.78rem', color: SAGE[400] }}>
          Posted {formatPostedDate(position.updated_at)}
        </Typography>
      </Box>
    </Stack>
  );

  const body = (
    <Stack spacing={3}>
      {position.summary && (
        <Box>
          <Typography sx={{ fontFamily: F, fontSize: '0.65rem', fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: SAGE[500], mb: 1 }}>
            Role overview
          </Typography>
          <Typography sx={{ fontFamily: SERIF, fontSize: '0.95rem', lineHeight: 1.6, color: SAGE[700] }}>
            {position.summary}
          </Typography>
        </Box>
      )}

      {position.requirements && position.requirements.length > 0 && (
        <Box>
          <Typography sx={{ fontFamily: F, fontSize: '0.65rem', fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: SAGE[500], mb: 1 }}>
            What they're looking for · {position.requirements.length}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {position.requirements.map((r, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Box sx={{ mt: 0.85, width: 5, height: 5, borderRadius: '50%', bgcolor: SAGE[500], flexShrink: 0 }} />
                <Typography sx={{ fontFamily: F, fontSize: '0.85rem', color: SAGE[700], lineHeight: 1.5 }}>
                  {r}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {position.qualifications && position.qualifications.length > 0 && (
        <Box>
          <Typography sx={{ fontFamily: F, fontSize: '0.65rem', fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: SAGE[500], mb: 1 }}>
            Nice to have
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {position.qualifications.map((r, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Box sx={{ mt: 0.85, width: 5, height: 5, borderRadius: '50%', bgcolor: SAGE[300], flexShrink: 0 }} />
                <Typography sx={{ fontFamily: F, fontSize: '0.85rem', color: SAGE[600], lineHeight: 1.5 }}>
                  {r}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Stack>
  );

  const footerSelectionArea = (
    <>
      <SelectionToggle state={state} onCycle={() => cycle(id)} />
      <Typography sx={{ fontFamily: F, fontSize: '0.78rem', color: SAGE[500] }}>
        {state === 'saved' ? 'Saved to your list' : state === 'considering' ? 'Marked considering' : 'Save it or move on'}
      </Typography>
    </>
  );

  const footerPrimaryAction = (
    <Stack direction="row" spacing={1.25}>
      {position.url && (
        <Button
          href={position.url}
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
          sx={{
            fontFamily: F, fontSize: '0.78rem', fontWeight: 600,
            color: SAGE[600], textTransform: 'none',
            '&:hover': { bgcolor: 'transparent', color: SAGE[800] },
          }}
        >
          Source posting
        </Button>
      )}
      <Button
        variant="contained"
        startIcon={<LaunchIcon sx={{ fontSize: 14 }} />}
        onClick={() => {
          onClose();
          navigate(`/applications/preview/${position.company_domain}/${position.job_id}`);
        }}
        sx={{
          fontFamily: F, fontSize: '0.8rem', fontWeight: 600,
          bgcolor: SAGE[700], color: CREAM[50], textTransform: 'none', borderRadius: 2,
          '&:hover': { bgcolor: SAGE[800] },
        }}
      >
        Open full posting
      </Button>
    </Stack>
  );

  return (
    <EntityDetailModal
      open={open}
      onClose={onClose}
      header={header}
      body={body}
      footerSelectionArea={footerSelectionArea}
      footerPrimaryAction={footerPrimaryAction}
      maxWidth="md"
    />
  );
};

export default PositionDetailModal;
